
import { Conversation } from '@/contexts/ChatContext';

// Standardized naming for chat initiation
export const CHAT_ROLE = {
  COMMUNITY_MEMBER: 'community_member',
  BUSINESS_OWNER: 'business_owner'
} as const;

export type ChatRole = typeof CHAT_ROLE[keyof typeof CHAT_ROLE];

/**
 * Determines the current user's role in a conversation
 */
export const getUserChatRole = (conversation: Conversation, userId: string): ChatRole => {
  return conversation.member_id === userId
    ? CHAT_ROLE.COMMUNITY_MEMBER
    : CHAT_ROLE.BUSINESS_OWNER;
};

/**
 * Determines if the current user is a community member in the conversation
 */
export const isUserCommunityMember = (conversation: Conversation, userId: string): boolean => {
  return getUserChatRole(conversation, userId) === CHAT_ROLE.COMMUNITY_MEMBER;
};

/**
 * Converts from the old isUserInitiatedChat boolean to the new ChatRole type
 */
export const convertLegacyFlag = (isUserInitiatedChat: boolean): ChatRole => {
  return isUserInitiatedChat ? CHAT_ROLE.COMMUNITY_MEMBER : CHAT_ROLE.BUSINESS_OWNER;
};

/**
 * Gets the counterpart ID based on the user's role
 */
export const getCounterpartId = (conversation: Conversation, userRole: ChatRole): string => {
  return userRole === CHAT_ROLE.COMMUNITY_MEMBER
    ? conversation.business_id
    : conversation.member_id;
};

/**
 * Gets the counterpart name based on the user's role - prioritizes username for members
 */
export const getCounterpartName = (conversation: Conversation, userRole: ChatRole): string => {
  if (userRole === CHAT_ROLE.COMMUNITY_MEMBER) {
    return conversation.business?.name || 'Business';
  } else {
    const member = conversation.member;
    // Priority: username > email > fallback
    return member?.username || member?.email || 'User';
  }
};

/**
 * Gets the counterpart logo based on the user's role
 */
export const getCounterpartLogo = (conversation: Conversation, userRole: ChatRole): string | null => {
  return userRole === CHAT_ROLE.COMMUNITY_MEMBER
    ? conversation.business?.company_logo || null
    : conversation.member?.avatar_url || null;
};
